import React, { useState, useEffect, useCallback } from 'react';
import { branchPageAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import './BranchPages.css';

/**
 * Branch Pages — custom, admin-configurable WiFi-management pages (Staff,
 * Banquet, PreCheckin, restaurant, etc) alongside the dedicated Rooms page.
 * One consolidated screen: manage the list of pages for this branch (max 5),
 * pick one via tabs, and manage/check-in/check-out its entries.
 *
 * Note: unlike the reference design's separate sidebar item per page, these
 * live under this single "Branch Pages" nav entry with a tab switcher —
 * this app's routes are static, so dynamic per-page sidebar items (a new
 * route appearing the moment an admin types a new page name) would need a
 * larger routing change. Flag if that's actually required.
 */
const BranchPages = () => {
    const { selectedBranch } = useBranchStore((state) => ({ selectedBranch: state.selectedBranch }));

    const [pages, setPages] = useState([]);
    const [activePageId, setActivePageId] = useState(null);
    const [entries, setEntries] = useState([]);
    const [loadingPages, setLoadingPages] = useState(true);
    const [loadingEntries, setLoadingEntries] = useState(false);

    const [showAddPage, setShowAddPage] = useState(false);
    const [newPageName, setNewPageName] = useState('');

    const [showAddEntry, setShowAddEntry] = useState(false);
    const [newEntryName, setNewEntryName] = useState('');

    const [checkInEntry, setCheckInEntry] = useState(null); // entry being checked in
    const [checkInForm, setCheckInForm] = useState({ name: '', regCard: '', vip: 'normal', email: '', phone: '' });

    const fetchPages = useCallback(async () => {
        if (!selectedBranch?._id) return;
        setLoadingPages(true);
        try {
            const res = await branchPageAPI.getAll(selectedBranch._id);
            const list = res.data.data || [];
            setPages(list);
            if (list.length && !list.find(p => p._id === activePageId)) {
                setActivePageId(list[0]._id);
            }
            if (!list.length) setActivePageId(null);
        } catch (err) {
            toast.error('Failed to load branch pages');
        } finally {
            setLoadingPages(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBranch?._id]);

    const fetchEntries = useCallback(async (pageId) => {
        if (!pageId) { setEntries([]); return; }
        setLoadingEntries(true);
        try {
            const res = await branchPageAPI.getEntries(pageId);
            setEntries(res.data.data || []);
        } catch (err) {
            toast.error('Failed to load entries');
        } finally {
            setLoadingEntries(false);
        }
    }, []);

    useEffect(() => { fetchPages(); }, [fetchPages]);
    useEffect(() => { fetchEntries(activePageId); }, [activePageId, fetchEntries]);

    const handleAddPage = async (e) => {
        e.preventDefault();
        if (!newPageName.trim()) return;
        try {
            const res = await branchPageAPI.create({
                tenantId: selectedBranch.tenantId?._id || selectedBranch.tenantId,
                branchId: selectedBranch._id,
                name: newPageName.trim(),
            });
            toast.success('Page created');
            setShowAddPage(false);
            setNewPageName('');
            await fetchPages();
            setActivePageId(res.data.data._id);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create page');
        }
    };

    const handleTogglePage = async (page) => {
        try {
            await branchPageAPI.update(page._id, { isActive: !page.isActive });
            fetchPages();
        } catch {
            toast.error('Failed to update page');
        }
    };

    const handleDeletePage = async (page) => {
        if (!window.confirm(`Delete "${page.name}" and all its entries?`)) return;
        try {
            await branchPageAPI.delete(page._id);
            toast.success('Page deleted');
            if (activePageId === page._id) setActivePageId(null);
            fetchPages();
        } catch {
            toast.error('Failed to delete page');
        }
    };

    const handleAddEntry = async (e) => {
        e.preventDefault();
        if (!newEntryName.trim()) return;
        try {
            await branchPageAPI.createEntry(activePageId, { name: newEntryName.trim() });
            setShowAddEntry(false);
            setNewEntryName('');
            fetchEntries(activePageId);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add entry');
        }
    };

    const handleDeleteEntry = async (entry) => {
        if (!window.confirm(`Delete "${entry.name}"?`)) return;
        try {
            await branchPageAPI.deleteEntry(entry._id);
            fetchEntries(activePageId);
        } catch {
            toast.error('Failed to delete entry');
        }
    };

    const openCheckIn = (entry) => {
        setCheckInEntry(entry);
        setCheckInForm({ name: '', regCard: '', vip: 'normal', email: '', phone: '' });
    };

    const handleCheckIn = async (e) => {
        e.preventDefault();
        if (!checkInForm.name.trim()) return toast.warning('Guest name is required');
        try {
            await branchPageAPI.checkInEntry(checkInEntry._id, checkInForm);
            toast.success('Checked in');
            setCheckInEntry(null);
            fetchEntries(activePageId);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Check-in failed');
        }
    };

    const handleCheckOut = async (entry) => {
        try {
            await branchPageAPI.checkOutEntry(entry._id);
            toast.success('Checked out');
            fetchEntries(activePageId);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Check-out failed');
        }
    };

    const generateCode = () => {
        const code = `WIFI-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
        setCheckInForm(f => ({ ...f, regCard: code }));
    };

    if (!selectedBranch?._id) {
        return <div className="bp-empty">Select a branch to manage its custom pages.</div>;
    }

    const activePage = pages.find(p => p._id === activePageId);

    return (
        <div className="bp-wrapper">
            <div className="bp-header">
                <h1>Branch Pages</h1>
                <p>Configure up to {5} custom WiFi-management pages for this branch, each with its own check-in entries.</p>
            </div>

            {/* Page tabs + manage list */}
            <div className="bp-card">
                <div className="bp-card-header">
                    <h2>Pages ({pages.length}/5)</h2>
                    {pages.length < 5 && (
                        <button className="btn-primary" onClick={() => setShowAddPage(true)}>+ Add Page</button>
                    )}
                </div>
                {loadingPages ? (
                    <div className="bp-loading">Loading…</div>
                ) : pages.length === 0 ? (
                    <div className="bp-loading">No custom pages yet.</div>
                ) : (
                    <div className="bp-page-list">
                        {pages.map(p => (
                            <div key={p._id} className={`bp-page-row ${p._id === activePageId ? 'active' : ''}`}>
                                <button className="bp-page-select" onClick={() => setActivePageId(p._id)}>
                                    <strong>{p.name}</strong> <code>/{p.slug}</code>
                                    <span className={`bp-badge ${p.isActive ? 'bp-badge-green' : 'bp-badge-grey'}`}>
                                        {p.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </button>
                                <div className="bp-page-actions">
                                    <button title="Toggle active" onClick={() => handleTogglePage(p)}><i className="fas fa-power-off"></i></button>
                                    <button title="Delete" onClick={() => handleDeletePage(p)}><i className="fas fa-trash"></i></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Entries for the active page */}
            {activePage && (
                <div className="bp-card">
                    <div className="bp-card-header">
                        <h2>{activePage.name} ({entries.length})</h2>
                        <button className="btn-primary" onClick={() => setShowAddEntry(true)}>+ Add {activePage.name} Entry</button>
                    </div>
                    {loadingEntries ? (
                        <div className="bp-loading">Loading…</div>
                    ) : entries.length === 0 ? (
                        <div className="bp-loading">No entries yet on this page.</div>
                    ) : (
                        <div className="bp-entry-grid">
                            {entries.map(e => (
                                <button
                                    key={e._id}
                                    className={`bp-entry-badge ${e.status === 'occupied' ? 'bp-badge-red' : 'bp-badge-green'}`}
                                    onClick={() => e.status === 'occupied' ? handleCheckOut(e) : openCheckIn(e)}
                                    onContextMenu={(ev) => { ev.preventDefault(); handleDeleteEntry(e); }}
                                    title={e.status === 'occupied' ? `${e.currentGuest?.name || ''} — click to check out` : 'Click to check in — right-click to delete'}
                                >
                                    {e.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Add Page modal */}
            {showAddPage && (
                <div className="bp-modal-overlay" onClick={() => setShowAddPage(false)}>
                    <div className="bp-modal" onClick={ev => ev.stopPropagation()}>
                        <h3>Add Custom Page</h3>
                        <form onSubmit={handleAddPage}>
                            <label>Page Name *</label>
                            <input autoFocus type="text" value={newPageName} onChange={e => setNewPageName(e.target.value)}
                                placeholder="e.g. Conference Rooms" />
                            <p className="bp-hint">Displayed in the page list (e.g. "Conference Rooms").</p>
                            <div className="bp-modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setShowAddPage(false)}>Cancel</button>
                                <button type="submit" className="btn-primary" disabled={!newPageName.trim()}>Create Page</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Entry modal */}
            {showAddEntry && (
                <div className="bp-modal-overlay" onClick={() => setShowAddEntry(false)}>
                    <div className="bp-modal" onClick={ev => ev.stopPropagation()}>
                        <h3>Add {activePage?.name} Entry</h3>
                        <form onSubmit={handleAddEntry}>
                            <label>Name *</label>
                            <input autoFocus type="text" value={newEntryName} onChange={e => setNewEntryName(e.target.value)}
                                placeholder="e.g. venue1, RECEPTION, 1009" />
                            <div className="bp-modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setShowAddEntry(false)}>Cancel</button>
                                <button type="submit" className="btn-primary" disabled={!newEntryName.trim()}>Add</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Check-in modal */}
            {checkInEntry && (
                <div className="bp-modal-overlay" onClick={() => setCheckInEntry(null)}>
                    <div className="bp-modal" onClick={ev => ev.stopPropagation()}>
                        <div className="bp-modal-title-row">
                            <h3>{activePage?.name} No: {checkInEntry.name}</h3>
                            <button className="bp-close" onClick={() => setCheckInEntry(null)}>&times;</button>
                        </div>
                        <button type="button" className="btn-primary bp-full" onClick={generateCode}>
                            <i className="fas fa-sync-alt"></i> Generate Custom Code
                        </button>
                        <form onSubmit={handleCheckIn}>
                            <input type="text" placeholder="Guest Name *" value={checkInForm.name}
                                onChange={e => setCheckInForm(f => ({ ...f, name: e.target.value }))} />
                            <div className="bp-form-row">
                                <div>
                                    <input type="text" placeholder="Reg Card" value={checkInForm.regCard}
                                        onChange={e => setCheckInForm(f => ({ ...f, regCard: e.target.value }))} />
                                    <p className="bp-hint">Optional (Auto-generated if empty)</p>
                                </div>
                                <select value={checkInForm.vip} onChange={e => setCheckInForm(f => ({ ...f, vip: e.target.value }))}>
                                    <option value="normal">Normal</option>
                                    <option value="vip">VIP</option>
                                </select>
                            </div>
                            <div className="bp-form-row">
                                <input type="email" placeholder="Email" value={checkInForm.email}
                                    onChange={e => setCheckInForm(f => ({ ...f, email: e.target.value }))} />
                                <input type="text" placeholder="Phone" value={checkInForm.phone}
                                    onChange={e => setCheckInForm(f => ({ ...f, phone: e.target.value }))} />
                            </div>
                            <div className="bp-modal-actions">
                                <button type="submit" className="btn-primary bp-full"><i className="fas fa-check-circle"></i> Check In</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BranchPages;
